 
// /home/project/src/components/Layout/Header.tsx
/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * AX1 PREMIUM HEADER — World-Class App Chrome (2026)
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Premium glass header with scroll detection, 3-zone layout, and consistent spacing.
 * Aligns to PAGE tokens for unified design system.
 */
import { AppIcon } from '@/components/ui/icons';
import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import RoleSwitcher from '../RoleSwitcher';
import { InboxDropdown } from '../inbox';
import BrandLogo from '../BrandLogo';
import { QuickActionsPanel } from '../QuickActionsPanel';
import { GlobalSearchModal } from '../GlobalSearchModal';
import { CommandPalette } from '../CommandPalette';
import Tooltip from '../Tooltip';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { PAGE } from '../../lib/layout/layoutTokens';
import { getExecutionMode, getExecutionModeLabel } from '../../lib/runtime/executionMode';
import { resolveIdentityRoles } from '../../lib/auth/identityRoles';
import { getMvpAccountRoleLabel } from '../../lib/auth/accountRoles';
import { APP_NAVIGATION_ID, APP_NAVIGATION_TOGGLE_ID } from '../../hooks/useNavigationShellState';
import { IS_DEMO_MODE } from '@/config/env';
import { useActiveWorkspaceId } from '@/hooks/useActiveWorkspaceId';
import { getAvailableQuickActionIds } from '@/lib/navigation/quickActionAvailability';
import { resolveScopedCapacityLabel } from '@/lib/layout/resolveScopedCapacityLabel';
import { Avatar } from '@/features/ui/components/Avatar';

interface HeaderProps {
  onToggleSidebar: () => void;
  isSidebarOpen?: boolean;
  isSidebarCollapsed?: boolean;
  isMobileNavigation?: boolean;
  className?: string;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  isSidebarOpen = true,
  isSidebarCollapsed = false,
  isMobileNavigation = false,
  className,
}) => {
  const { user } = useAuth();
  const _navigate = useNavigate();
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const activeWorkspaceId = useActiveWorkspaceId();
  const hasQuickActions = getAvailableQuickActionIds(user, activeWorkspaceId).length > 0;

  // Scroll detection for subtle shadow
  const [isScrolled, setIsScrolled] = useState(false);
  const executionMode = getExecutionMode();
  const executionModeLabel = getExecutionModeLabel(executionMode);
  // Environment is context, not health. Keep every mode visually neutral so
  // production never implies "healthy" and staging never implies "warning".
  const executionModeClasses = 'bg-white/[0.04] text-text-muted border-white/[0.10]';
  const resolvedIdentity = resolveIdentityRoles(user);
  const canonicalAccountRole = resolvedIdentity.accountRole ?? resolvedIdentity.platformRole;
  const accountRoleLabel = canonicalAccountRole
    ? getMvpAccountRoleLabel(canonicalAccountRole)
    : 'Role unavailable';
  const operatingCapacityLabel = canonicalAccountRole === 'organization_member'
    ? resolveScopedCapacityLabel(user)
    : null;
  const displayedRoleLabel = operatingCapacityLabel ?? accountRoleLabel;

  useEffect(() => {
    const handleScroll = () => {
      // Check if any scrollable main content is scrolled
      const mainContent = document.getElementById('main-content');
      setIsScrolled(mainContent ? mainContent.scrollTop > 0 : window.scrollY > 0);
    };

    // Listen to both window and main content scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    const mainContent = document.getElementById('main-content');
    mainContent?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      mainContent?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Command palette (Ctrl/Cmd + K)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Global search (Ctrl/Cmd + /)
      if (IS_DEMO_MODE && (e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowGlobalSearch(true);
      }
      // Quick actions (Ctrl/Cmd + Shift + A)
      if (hasQuickActions && (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowQuickActions(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasQuickActions]);

  return (
    <header
      className={cn(
        "ax1-app-header relative z-50 h-16 min-h-16 flex-none",
        isScrolled && 'ax1-app-header-scrolled',
        "transition-shadow duration-200",
        className
      )}
    >
      {/* ═══════════════════════════════════════════════════════════════════
          3-ZONE HEADER LAYOUT — Aligned to page container rhythm
          ═══════════════════════════════════════════════════════════════════ */}
      <div className={cn("h-full flex items-center justify-between mx-auto w-full", PAGE.padX)}>

        {/* ═══════════════════════════════════════════════════════════════════
            ZONE LEFT: Hamburger + Logo
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Sidebar Toggle */}
          <button
            id={APP_NAVIGATION_TOGGLE_ID}
            type="button"
            onClick={onToggleSidebar}
            className="ax1-shell-icon-button p-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
            aria-label={isMobileNavigation
              ? (isSidebarOpen ? 'Close navigation' : 'Open navigation')
              : (isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation')}
            aria-controls={APP_NAVIGATION_ID}
            aria-expanded={isMobileNavigation ? isSidebarOpen : !isSidebarCollapsed}
          >
            <AppIcon name="menu" className="w-5 h-5" />
          </button>

          {/* Logo */}
          <BrandLogo variant="full" className="ax1-header-brand" />
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            ZONE CENTER: Search Input (max-w-lg, centered)
            ═══════════════════════════════════════════════════════════════════ */}
        {IS_DEMO_MODE && <div className="hidden flex-1 justify-center px-4 md:flex lg:px-8">
          <div className="relative w-full max-w-lg">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <AppIcon name="search" className="w-4 h-4 text-text-muted" />
            </div>
            <input
              type="text"
              aria-label="Search"
              autoComplete="off"
              placeholder="Search... (Ctrl+/)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={() => setShowGlobalSearch(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchTerm.trim()) {
                  setShowGlobalSearch(true);
                }
              }}
              className={cn(
                "w-full h-9 pl-9 pr-4",
                "ax1-global-search rounded-lg text-sm",
                "focus:outline-none",
                "transition-all duration-150 cursor-pointer"
              )}
              readOnly
            />
          </div>
        </div>}

        {/* ═══════════════════════════════════════════════════════════════════
            ZONE RIGHT: Action Cluster — gap-3 between groups, gap-2 inside
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Execution Mode — explicit environment truth */}
          <Tooltip content={
            executionMode === 'production'
              ? 'Live execution mode. Production controls apply.'
              : executionMode === 'staging'
                ? 'Staging execution mode. Test-integrated environment.'
                : 'Demo execution mode. Simulation-first environment.'
          }>
            <div
              aria-label={`Execution mode: ${executionModeLabel}`}
              className={cn(
                'h-8 px-2.5 hidden sm:flex items-center gap-1.5',
                'border rounded-lg text-[11px] font-semibold tracking-[0.08em]',
                executionModeClasses,
              )}
            >
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-80" />
              <span>{executionModeLabel}</span>
            </div>
          </Tooltip>

          {/* Command Palette — gap-2 cluster */}
          <div className="flex items-center gap-2">
            <Tooltip content="Command Palette (Ctrl+K)">
              <button
                type="button"
                onClick={() => setShowCommandPalette(true)}
                className={cn(
                  "h-8 w-8 hidden sm:flex items-center justify-center",
                  "ax1-shell-icon-button rounded-lg",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                )}
                aria-label="Open command palette"
              >
                <AppIcon name="command" className="w-4 h-4" />
              </button>
            </Tooltip>

            {/* Quick Actions */}
            {hasQuickActions && <Tooltip content="Quick Actions (Ctrl+Shift+A)">
              <button
                type="button"
                onClick={() => setShowQuickActions(true)}
                className={cn(
                  "h-8 w-8 flex items-center justify-center",
                  "bg-primary-500 hover:bg-primary-600",
                  "text-white rounded-lg shadow-sm",
                  "transition-colors duration-150",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 focus-visible:ring-offset-2"
                )}
                aria-label="Quick actions"
              >
                <AppIcon name="plus" className="w-4 h-4" />
              </button>
            </Tooltip>}
          </div>

          {/* Unified Inbox */}
          <InboxDropdown className="ax1-header-inbox" />

          {/* User Menu */}
          <div className="relative">
            <Tooltip content={`${user?.name} - ${displayedRoleLabel}`}>
              <button
                type="button"
                onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                className={cn(
                  "flex items-center gap-2 p-1.5",
                  "ax1-shell-user rounded-lg",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                )}
                aria-label={`Open account menu for ${user?.name || 'AX1 user'}, ${displayedRoleLabel}`}
                aria-expanded={showRoleSwitcher}
                aria-haspopup="menu"
              >
                <Avatar
                  name={user?.name || 'AX1 user'}
                  src={user?.avatar}
                  size={32}
                  colorSeed={user?.id || user?.email || user?.name}
                  ring={false}
                  className="ring-1 ring-[color:var(--ax1-shell-border)]"
                  decorative
                />
                <div className="hidden lg:block text-left">
                  <p className="ax1-shell-text text-sm font-medium leading-tight">{user?.name}</p>
                  <p className="ax1-shell-muted text-xs">{displayedRoleLabel}</p>
                </div>
                <AppIcon name="chevronDown" className={cn(
                  "ax1-shell-muted w-4 h-4 hidden lg:block transition-transform duration-150",
                  showRoleSwitcher && "rotate-180"
                )} />
              </button>
            </Tooltip>

            <AnimatePresence>
              {showRoleSwitcher && (
                <RoleSwitcher onClose={() => setShowRoleSwitcher(false)} />
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════════════════ */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />

      {IS_DEMO_MODE && <GlobalSearchModal
        isOpen={showGlobalSearch}
        onClose={() => setShowGlobalSearch(false)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />}

      {hasQuickActions && <QuickActionsPanel
        isOpen={showQuickActions}
        onClose={() => setShowQuickActions(false)}
      />}
    </header>
  );
};

export default Header;
